import { Dispatch } from 'redux';
import types from 'redux/actions/actionTypes';
import { axiosV2 } from 'interceptors/axios';

import { TradingOffer } from 'types';
import { PublisherAuditClientContext } from 'modules/trading/utils/collectPublisherAuditContext';

import { decryptResponse } from 'utils/token';
import { dispatchError } from 'utils/dispatchError';
import { ApiQueryParams } from 'types/ApiQueryParams';
import { TradingOfferStatusCounts } from 'types/TradingOffer';
import { updateSocietyData } from '../../society/redux/updateSocietyData';

type GetTradingOffersProps = {
  societyId: string;
  params?: ApiQueryParams;
  signal?: AbortSignal;
  skipDispatch?: boolean;
};

type TradingOfferApiAddResponse = {
  tradingOffer: TradingOffer;
  tradingOffersCount: {
    buy: number;
    sell: number;
    total: number;
  };
};

type TradingOfferApiGetAllResponse = {
  data: TradingOffer[];
  total: number;
  statusCount: TradingOfferStatusCounts;
};

type TradingOfferApiMutationResponse = {
  tradingOffer: TradingOffer;
  tradingOffersCount?: {
    buy: number;
    sell: number;
    total: number;
  };
};

type UpdateTradingOfferPayload = Partial<
  Pick<
    TradingOffer,
    | 'sharePrice'
    | 'startDate'
    | 'endDate'
    | 'isPartialAmountEnabled'
    | 'minSharesPerAcceptance'
    | 'isSellerNameHidden'
    | 'emailSubsidiary'
    | 'documents'
  >
> & {
  sharesCount?: Pick<TradingOffer['sharesCount'], 'total'>;
};

type AddTradingOfferPayload = Partial<TradingOffer> & {
  publisherAuditContext?: PublisherAuditClientContext;
};

export const addTradingOffer =
  (tradingOfferData: AddTradingOfferPayload) => async (dispatch: Dispatch) => {
    try {
      const body = { ...tradingOfferData };
      const { data } = await axiosV2.post('/tradingOffers', body);
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferApiAddResponse;

      dispatch({
        type: types.CREATE_TRADING_OFFER,
        tradingOffer: decodedData?.tradingOffer,
      });

      updateSocietyData(decodedData, dispatch);

      return decodedData?.tradingOffer;
    } catch (error) {
      dispatchError(error, dispatch);
      throw error;
    }
  };

export const getTradingOffers =
  ({
    societyId,
    params = {},
    signal,
    skipDispatch = false,
  }: GetTradingOffersProps) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get('/tradingOffers', {
        params: {
          societyId,
          ...params,
        },
        // signal,
      });

      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferApiGetAllResponse;

      if (!skipDispatch) {
        dispatch({
          type: types.GET_TRADING_OFFERS,
          tradingOffers: decodedData.data,
        });
      }

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const getTradingOffer =
  (tradingOfferId: string, params?: ApiQueryParams) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get(`/tradingOffers/${tradingOfferId}`, {
        params,
      });
      const decodedData = (await decryptResponse(data)) as TradingOffer;

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
      throw error;
    }
  };

export const updateTradingOffer =
  (tradingOfferId: string, payload: UpdateTradingOfferPayload) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.put(
        `/tradingOffers/${tradingOfferId}`,
        payload
      );
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferApiMutationResponse;

      dispatch({
        type: types.UPDATE_TRADING_OFFER,
        tradingOffer: decodedData?.tradingOffer,
      });

      return decodedData?.tradingOffer;
    } catch (error) {
      dispatchError(error, dispatch);
      throw error;
    }
  };

export const cancelTradingOffer =
  (tradingOfferId: string) => async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.delete(`/tradingOffers/${tradingOfferId}`);
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferApiMutationResponse;

      dispatch({
        type: types.DELETE_TRADING_OFFER,
        tradingOfferId: decodedData?.tradingOffer?._id || tradingOfferId,
      });

      updateSocietyData(decodedData, dispatch);

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
      throw error;
    }
  };
